import { sanitizeUrl } from '@braintree/sanitize-url'

const SAFE_ATTACHMENT_URL_PATTERN = /^https?:\/\//i

export const sanitizeAttachmentUrl = url => {
  if (!url) return null

  const sanitized = sanitizeUrl(String(url).trim())
  if (!sanitized || sanitized === 'about:blank') return null
  if (!SAFE_ATTACHMENT_URL_PATTERN.test(sanitized)) return null

  return sanitized
}

export const formatMessageListTime = dateInput => {
  if (!dateInput) return ''

  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) return ''

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'now'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`

  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const getContactRoleLabel = user => {
  const relationship =
    user?.relationshipType ||
    user?.pga_relationship_type ||
    user?.pgaRelationshipType

  if (relationship === 'PARENT') return 'parent'

  const role = user?.role || user?.pga_role || user?.pgaRole
  if (typeof role === 'string' && role.toLowerCase() === 'parent') {
    return 'parent'
  }

  return null
}

export const getChannelLastMessageAt = channel => {
  const messages = channel?.state?.messages
  const lastMessage = messages?.[messages.length - 1]
  return lastMessage?.created_at || channel?.state?.last_message_at
}

export const getChannelLatestMessage = channel => {
  const latestMessages = channel?.state?.latestMessages
  if (latestMessages?.length) {
    return latestMessages[latestMessages.length - 1]
  }

  const messages = channel?.state?.messages
  if (messages?.length) {
    return messages[messages.length - 1]
  }

  return undefined
}

export const getMessagingContactProfilePath = contact => {
  if (!contact?.id || !contact?.type) return null

  switch (contact.type) {
    case 'STUDENT':
      return `/pga-coach/student/${contact.id}`
    case 'CONTACT':
      return `/pga-coach/leads/${contact.id}`
    default:
      return null
  }
}

export const buildMessagingContactsByExternalId = contacts => {
  const map = new Map()

  contacts?.forEach(contact => {
    if (contact.externalId) {
      map.set(contact.externalId, contact)
    }

    contact.externalIds?.forEach(externalId => {
      map.set(externalId, contact)
    })
  })

  return map
}

const normalizeUrlForComparison = url => {
  if (!url) return ''

  try {
    const normalized = new URL(
      /^https?:\/\//i.test(url) ? url : `https://${url}`,
    )

    return `${normalized.host.replace(/^www\./, '')}${normalized.pathname}${
      normalized.search
    }`
      .toLowerCase()
      .replace(/\/$/, '')
  } catch {
    return String(url)
      .trim()
      .toLowerCase()
      .replace(/\/$/, '')
  }
}

export const shouldSuppressCardUrlMessageText = message => {
  const scrapedAttachment = message.attachments?.find(
    att => att?.og_scrape_url || att?.title_link,
  )
  const trimmedText = message.text?.trim()

  if (!scrapedAttachment || !trimmedText) return false

  const cardUrl =
    scrapedAttachment.title_link || scrapedAttachment.og_scrape_url
  if (!cardUrl) return false

  if (
    normalizeUrlForComparison(trimmedText) ===
    normalizeUrlForComparison(cardUrl)
  ) {
    return true
  }

  if (!/^https?:\/\/\S+$/i.test(trimmedText)) {
    return false
  }

  try {
    const cardHost = new URL(
      /^https?:\/\//i.test(cardUrl) ? cardUrl : `https://${cardUrl}`,
    ).host.replace(/^www\./, '')
    const textHost = new URL(trimmedText).host.replace(/^www\./, '')

    return cardHost === textHost
  } catch {
    return false
  }
}

export const shortenUrlLabel = (url, maxLength = 40) => {
  const text = String(url || '').trim()
  if (!text) return text

  const withoutScheme = text.replace(/^https?:\/\//i, '')
  if (withoutScheme.length <= maxLength) return withoutScheme

  try {
    const { hostname, pathname, search } = new URL(
      /^https?:\/\//i.test(text) ? text : `https://${withoutScheme}`,
    )
    const host = hostname.replace(/^www\./, '')
    const path = `${pathname}${search}`

    if (!path || path === '/') {
      return host.length <= maxLength
        ? host
        : `${host.slice(0, maxLength - 1)}…`
    }

    const availableForPath = maxLength - host.length
    if (availableForPath <= 4) {
      return `${host.slice(0, maxLength - 1)}…`
    }

    const shortPath =
      path.length > availableForPath
        ? `${path.slice(0, availableForPath - 1)}…`
        : path

    return `${host}${shortPath}`
  } catch {
    return `${withoutScheme.slice(0, maxLength - 1)}…`
  }
}

export const formatChannelPreviewText = (text, maxLength = 80) => {
  const singleLine = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()

  if (!singleLine) return ''

  const withShortUrls = singleLine.replace(/https?:\/\/\S+/gi, url =>
    shortenUrlLabel(url, 40),
  )

  if (withShortUrls.length <= maxLength) {
    return withShortUrls
  }

  return `${withShortUrls.slice(0, maxLength - 1)}…`
}
